/**
 * App Component
 * Root: theme + i18n providers + Router (which mounts BrandResolver
 * and the path-based route tree).
 *
 * All brand resolution logic now lives in `routing/BrandResolver.tsx`.
 * Mounted inside the Router so it can read `useLocation`.
 */

import { useEffect, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import heIL from 'antd/locale/he_IL';
import Router from './Router';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/hooks/useLanguage';
import { getDarkTheme, getLightTheme } from '@/styles/theme';
import { applyCachedBrandSync } from './routing/BrandResolver';
import { VersionRefreshToast } from '@/components/VersionRefreshToast';
import { SupportFallbackButton } from '@/components/layout/SupportFallbackButton';
import './i18n';
import '@styles/global.css';
import '@styles/page-frame.css';

// Synchronously apply the previously-cached brand for the current URL
// scope so the first paint has the right CSS variables + title.
applyCachedBrandSync();

function App() {
  const { theme } = useTheme();
  const { direction, language } = useLanguage();

  // Antd ships its own strings for the widgets that render text we never
  // author: Table pagination ("items per page"), Empty ("No data"), the
  // DatePicker month/weekday names, Popconfirm's OK/Cancel, Select's
  // "Not Found", Transfer, Upload. Without a locale antd falls back to
  // en_US, so a Hebrew page kept leaking English out of those widgets no
  // matter how complete our own translation file was.
  const antdLocale = language === 'he' ? heIL : enUS;

  // Memoize the antd theme object — `getDarkTheme()/getLightTheme()`
  // returned a NEW object on every App render, which made
  // ConfigProvider's `theme` prop change reference constantly. Antd
  // reads this reference to drive context updates; combined with
  // direction changing (also via prop reference), every parent
  // re-render forced the entire Router subtree to re-evaluate. In
  // dev mode with StrictMode this multiplied into a visible
  // unmount → blank screen → remount cycle on every navigation.
  // Memoizing keeps the reference stable while only the actual
  // theme/direction VALUES change.
  const antdTheme = useMemo(
    () => (theme === 'dark' ? getDarkTheme() : getLightTheme()),
    [theme],
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('dir', direction);
  }, [theme, direction]);

  // App-wide keyboard-aware viewport.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const root = document.documentElement;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        root.style.setProperty('--chat-vvh', `${vv.height}px`);
        root.style.setProperty('--chat-vvtop', `${vv.offsetTop}px`);
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return (
    <ConfigProvider theme={antdTheme} direction={direction} locale={antdLocale}>
      <Router />
      <SupportFallbackButton />
      <VersionRefreshToast />
    </ConfigProvider>
  );
}

export default App;
